import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { EASE } from "@/lib/motion";
import { NAV_ITEMS, MARK_LIGHT } from "./TopNav";
import { GridLines } from "@/components/ui-custom/GridLines";

export const MobileMenu = ({ light, cfg }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="mobile-menu-open-button"
          aria-label="Menü öffnen"
          className={cn("group flex h-10 w-10 flex-col items-end justify-center gap-[6px] lg:hidden", light ? "text-paper" : "text-ink")}
        >
          <span className="block h-px w-7 bg-current transition-[width] duration-300 ease-arch group-hover:w-5" />
          <span className="block h-px w-5 bg-current transition-[width] duration-300 ease-arch group-hover:w-7" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        data-testid="mobile-menu-sheet"
        className="w-full sm:max-w-none border-0 bg-ink p-0 text-paper [&>button]:hidden"
      >
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <SheetDescription className="sr-only">Hauptnavigation und Kontaktinformationen</SheetDescription>
        <div className="relative flex h-full flex-col">
          <GridLines dark />
          <div className="container-x relative flex h-[72px] items-center justify-between">
            <img src={MARK_LIGHT} alt="L&A" width={791} height={314} draggable={false} className="block h-[24px] w-auto select-none" />
            <button
              type="button"
              data-testid="mobile-menu-close-button"
              aria-label="Menü schliessen"
              onClick={() => setOpen(false)}
              className="mono-label flex h-10 items-center gap-3 text-paper/70 hover:text-paper"
            >
              Schliessen
              <span className="relative block h-4 w-4">
                <span className="absolute left-0 top-1/2 h-px w-full rotate-45 bg-current" />
                <span className="absolute left-0 top-1/2 h-px w-full -rotate-45 bg-current" />
              </span>
            </button>
          </div>

          <nav aria-label="Mobile Navigation" className="container-x relative flex flex-1 flex-col justify-center gap-1 py-8">
            {[{ to: "/", label: "Home", index: "00" }, ...NAV_ITEMS, { to: "/industrie", label: "Industrie", index: "05" }].map((item, i) => {
              const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
              return (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.15 + i * 0.06 }}
                  className="border-b hl-light"
                >
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-menu-link-${item.label.toLowerCase()}`}
                    className="flex items-baseline justify-between py-4"
                  >
                    <span className={cn("headline text-[clamp(2.4rem,11vw,4.5rem)]", active ? "text-paper" : "text-paper/85")}>{item.label}</span>
                    <span className="mono-label text-paper/45">{item.index}</span>
                  </Link>
                </motion.div>
              );
            })}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
              className="mt-8"
            >
              <Link
                to="/kontakt"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-project-request-button"
                className="inline-flex h-12 items-center bg-paper px-6 font-heading text-[12px] font-semibold uppercase tracking-[0.14em] text-ink"
              >
                Projekt anfragen
              </Link>
            </motion.div>
          </nav>

          <div className="container-x relative grid grid-cols-2 gap-6 border-t hl-light py-6 mono-sm text-paper/60">
            <div>
              <p className="mono-label mb-2 text-paper/40">Standort</p>
              {cfg ? (
                <p>
                  {cfg.address.street}
                  <br />
                  {cfg.address.zip} {cfg.address.city}
                </p>
              ) : (
                <p>Neftenbach, Schweiz</p>
              )}
            </div>
            <div>
              <p className="mono-label mb-2 text-paper/40">Koordinaten</p>
              <p>{cfg?.coordinates?.dms || "47°31'N 8°40'E"}</p>
              <p className="mt-1">PROJECT / CH</p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
